// @flow
import {createSelector} from "reselect";
import isEmpty from "lodash/isEmpty";
import TokenDataSelectors from "../TokenData/selectors";
import ServiceConfigSelectors from "../ServiceConfig/selectors";
import {getPolygonIfLatLngInPolygon} from "../../../Utils/MapUtils";
import type {State} from "../../Store";
import MapDataSelectors from "../MapData/selectors";
import {StopSelection} from "./utils";

const currentRequestSelector = (state: State) => state.currentRequest;

const getOriginAirportTerminals = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return currentRequest.originTerminals;
    }
);

const getDestAirportTerminals = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return currentRequest.destTerminals;
    }
);

const getOriginAddress = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return currentRequest.origin_address;
    }
);

const getDestAddress = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return currentRequest.dest_address;
    }
);

const getPassengerCount = createSelector(
    currentRequestSelector,
    (currentRequest): number => {
        return currentRequest.passenger_count;
    }
);

const currentRequestReady = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return (currentRequest && !isEmpty(currentRequest));
    }
);


const getOrigin = createSelector(
    currentRequestSelector,
    getOriginAddress,
    MapDataSelectors.getMapCenter,
    TokenDataSelectors.getTokenOrigin,
    (currentRequest, originAddress, mapCenter, tokenOrigin) => {
        return {
            lat: currentRequest.origin_lat || tokenOrigin.lat || mapCenter.lat,
            lng: currentRequest.origin_lng || tokenOrigin.lng || mapCenter.lng,
            address: originAddress,
            selection: currentRequest.origin_selection,
        };
    }
);

const getOriginLat = createSelector(
    getOrigin,
    (origin) => {
        return origin.lat;
    }
);

const getOriginLng = createSelector(
    getOrigin,
    (origin) => {
        return origin.lng;
    }
);

const getDest = createSelector(
    currentRequestSelector,
    TokenDataSelectors.tokenDataSelector,
    getDestAddress,
    MapDataSelectors.getMapCenter,
    TokenDataSelectors.getTokenDest,
    (currentRequest, tokenData, destAddress, mapCenter, tokenDest) => {
        return {
            lat: currentRequest.dest_lat || tokenDest.lat || (mapCenter.lat + 0.005),
            lng: currentRequest.dest_lng || tokenDest.lng || (mapCenter.lng + 0.005),
            address: destAddress,
            selection: currentRequest.dest_selection,
        };
    }
);

const getDestLat = createSelector(
    getDest,
    (dest) => {
        return dest.lat;
    }
);

const getDestLng = createSelector(
    getDest,
    (dest) => {
        return dest.lng;
    }
);

const isOriginSelected = createSelector(
    getOrigin,
    (origin) => {
        return origin.selection && (origin.selection !== StopSelection.NONE);
    }
);

const isDestSelected = createSelector(
    getDest,
    (dest) => {
        return dest.selection && (dest.selection !== StopSelection.NONE);
    }
);

const isSearchingAddress = createSelector(
    currentRequestSelector,
    (currentRequest) => {
        return currentRequest && (currentRequest.dest_search || currentRequest.origin_search);
    }
);

const getOriginPolygonSelector = createSelector(
    getOrigin,
    ServiceConfigSelectors.getServicePolygons,
    (origin, servicePolygons) => {
        return getPolygonIfLatLngInPolygon(
            {
                lat: origin.lat,
                lng: origin.lng
            }, servicePolygons);
    }
);
const getDestPolygonSelector = createSelector(
    getDest,
    ServiceConfigSelectors.getServicePolygons,
    (dest, servicePolygons) => {
        return getPolygonIfLatLngInPolygon(
            {
                lat: dest.lat,
                lng: dest.lng
            }, servicePolygons);
    }
);

const isSearchingDest = createSelector(
    currentRequestSelector,

    (currentRequest) => {
        return !!(currentRequest.dest_search);
    },
);

const isSearchingOrigin = createSelector(
    currentRequestSelector,

    (currentRequest) => {
        return !!(currentRequest.origin_search);
    },
);
const isFreeSelectionDragging = createSelector(
    isSearchingOrigin,
    isSearchingDest,
    (searchingOrigin, searchingDest) => {
        return !(searchingOrigin || searchingDest);
    }
);

const shouldPanMapToOrigin = createSelector(
    TokenDataSelectors.getTokenOrigin,
    getOrigin,
    (tokenOrigin, origin) => {
        return (tokenOrigin.lat === origin.lat && tokenOrigin.lng === origin.lng);
    }
);

const shouldPanMapToDest = createSelector(
    TokenDataSelectors.getTokenDest,
    getDest,
    (tokenDest, dest) => {
        return (tokenDest.lat === dest.lat && tokenDest.lng === dest.lng);
    }
);

export default {
    currentRequestSelector,
    getOriginAirportTerminals,
    getDestAirportTerminals,
    getOriginAddress,
    getDestAddress,
    getPassengerCount,
    currentRequestReady,
    isOriginSelected,
    isDestSelected,
    getOrigin,
    getOriginLat,
    getOriginLng,
    getDest,
    isSearchingAddress,
    getOriginPolygonSelector,
    getDestPolygonSelector,
    isSearchingDest,
    isSearchingOrigin,
    isFreeSelectionDragging,
    shouldPanMapToOrigin,
    shouldPanMapToDest,
    getDestLat,
    getDestLng,
};


// WEBPACK FOOTER //
// ./src/Store/Ducks/CurrentRequest/selectors.js